import { useAuth } from "authentication/AuthProvider";

import {
	DeleteRequest,
	FetchRequest,
	GetRequest,
	ModifyRequest
} from "./FetchRequest";

export default function useFetch() {
	const { getBearerToken } = useAuth();

	async function fetchAuthenticated(
		input: RequestInfo,
		requestInit: RequestInit
	): Promise<Response> {
		await setAuthorizationHeader(requestInit);
		return fetch(input, requestInit);
	}

	async function setAuthorizationHeader(requestInit: RequestInit) {
		const accessToken = await getBearerToken();
		requestInit.headers = {
			...requestInit.headers,
			Authorization: "Bearer " + accessToken
		};
	}

	async function call<B, R>(request: FetchRequest<B, R>): Promise<R> {
		const result = await execute(request);
		checkStatus(result, request.statusCode);
		return request.callback(result);
	}

	function execute<B, R>(request: FetchRequest<B, R>): Promise<Response> {
		switch (request.operation) {
			case "GET":
				return get(request);
			case "POST":
			case "PUT":
			case "PATCH":
				return modify(request);
			case "DELETE":
				return del(request);
			default:
				throw new Error("Unsupported operation");
		}
	}

	function get<R>(req: GetRequest<R>): Promise<Response> {
		const f = req.unauthenticated ? fetch : fetchAuthenticated;
		return f(req.url, {});
	}

	function modify<B, R>(req: ModifyRequest<B, R>): Promise<Response> {
		if (req.body instanceof FormData) {
			return fetchAuthenticated(req.url, {
				method: req.operation,
				body: req.body
			});
		}

		const requestInit = {
			method: req.operation,
			body: JSON.stringify(req.body),
			headers: {
				"Content-Type": "application/json"
			}
		};

		const f = req.unauthenticated ? fetch : fetchAuthenticated;
		return f(req.url, requestInit);
	}

	function del<R>(req: DeleteRequest<R>): Promise<Response> {
		const requestInit = {
			method: "DELETE"
		};

		const f = req.unauthenticated ? fetch : fetchAuthenticated;
		return f(req.url, requestInit);
	}

	function checkStatus(result: Response, statusCode: number) {
		if (result.status !== statusCode) {
			throw new Error(result.statusText);
		}
	}

	return { call };
}
